import React, { useState } from 'react';

import clsx from 'clsx';
import i18n from 'i18n-react';
import Select from 'react-select';

import type { Category, Subcategory } from 'hooks/useDirectoryData/types';

interface SelectOption {
  value: string;
  label: string;
}

interface CategoryFilterProps {
  categories: Category[];
  selectedCategory: Category | null;
  selectedSubcategory: Subcategory | null;
  filterOpen: boolean;
  onCategorySelect: (category: Category | null) => void;
  onSubcategorySelect: (subcategory: Subcategory | null) => void;
  onSubmit?: (category: Category | null, subcategory: Subcategory | null) => void;
  onResetFilters: () => void;
  onToggleFilter: () => void;
  variant?: 'checkbox' | 'inline' | 'dropdown';
  // Receives the category the filters currently show: the pending one in the inline variant, the committed one otherwise.
  children?: (activeCategory: Category | null) => React.ReactNode;
}

function CategoryFilter({
  categories,
  selectedCategory,
  selectedSubcategory,
  filterOpen,
  onCategorySelect,
  onSubcategorySelect,
  onSubmit,
  onResetFilters,
  onToggleFilter,
  variant = 'checkbox',
  children,
}: CategoryFilterProps) {
  const [pendingCategory, setPendingCategory] = useState<Category | null>(selectedCategory);
  const [pendingSubcategory, setPendingSubcategory] = useState<Subcategory | null>(selectedSubcategory);
  const [prevFilterOpen, setPrevFilterOpen] = useState(filterOpen);
  const hasCategories = categories.length > 0;

  if (variant === 'dropdown' && filterOpen && !prevFilterOpen) {
    setPrevFilterOpen(true);
    setPendingCategory(selectedCategory);
    setPendingSubcategory(selectedSubcategory);
  } else if (!filterOpen && prevFilterOpen) {
    setPrevFilterOpen(false);
  }

  if (variant === 'dropdown') {
    const subcategories = selectedCategory?.subcategories ?? [];
    const categoryOptions = categories.map((cat) => {
      return { value: cat.cat_name, label: cat.cat_name_translated };
    });
    const subcategoryOptions = subcategories.map((sub) => {
      return {
        value: sub.sub_catname,
        label: sub.sub_catname_translated,
      };
    });

    const handleCategoryChange = (option: SelectOption | null | undefined) => {
      const cat =
        categories.find((c) => {
          return c.cat_name === option?.value;
        }) ?? null;
      onCategorySelect(cat);
    };

    const handleSubcategoryChange = (option: SelectOption | null | undefined) => {
      const sub =
        subcategories.find((s) => {
          return s.sub_catname === option?.value;
        }) ?? null;
      onSubcategorySelect(sub);
    };

    return (
      <div className={clsx('filterContainer', 'filterContainer--dropdown', { 'filterContainer--active': filterOpen })}>
        {hasCategories && (
          <Select
            isClearable
            isSearchable={false}
            classNamePrefix="reactSelect"
            placeholder={i18n.translate('directoryView.category') as string}
            options={categoryOptions}
            value={
              categoryOptions.find((option) => {
                return option.value === selectedCategory?.cat_name;
              }) ?? null
            }
            onChange={handleCategoryChange}
          />
        )}

        {subcategories.length > 0 && (
          <Select
            isClearable
            isSearchable={false}
            classNamePrefix="reactSelect"
            placeholder={i18n.translate('directoryView.subcategory') as string}
            options={subcategoryOptions}
            value={
              subcategoryOptions.find((option) => {
                return option.value === selectedSubcategory?.sub_catname;
              }) ?? null
            }
            onChange={handleSubcategoryChange}
          />
        )}

        {children?.(selectedCategory)}

        <div className="resetFilters" onClick={onResetFilters}>
          {i18n.translate('directoryView.resetFilters')}
        </div>
      </div>
    );
  }

  if (variant === 'inline') {
    const pendingSubcategories = pendingCategory?.subcategories ?? [];
    const categoryOptions = categories.map((cat) => {
      return { value: cat.cat_name, label: cat.cat_name_translated };
    });
    const pendingSubcategoryOptions = pendingSubcategories.map((sub) => {
      return {
        value: sub.sub_catname,
        label: sub.sub_catname_translated,
      };
    });

    const handleCategoryChange = (option: SelectOption | null | undefined) => {
      const cat =
        categories.find((c) => {
          return c.cat_name === option?.value;
        }) ?? null;
      setPendingCategory(cat);
      setPendingSubcategory(null);
    };

    const handleSubcategoryChange = (option: SelectOption | null | undefined) => {
      const sub =
        pendingSubcategories.find((s) => {
          return s.sub_catname === option?.value;
        }) ?? null;
      setPendingSubcategory(sub);
    };

    const handleSubmit = () => {
      const sub = pendingCategory ? pendingSubcategory : null;
      if (onSubmit) {
        onSubmit(pendingCategory, sub);
      } else {
        onCategorySelect(pendingCategory);
        onSubcategorySelect(sub);
      }
    };

    const handleClear = () => {
      setPendingCategory(null);
      setPendingSubcategory(null);
      onResetFilters();
      onToggleFilter();
    };

    return (
      // The 'overlap' classname is mutually exclusive with 'expand'. 'expand' pushes content (listings)
      // down in normal document flow. Due to requests mobile, overlap changes this to be a full height
      // absolute/overlap implementation tho prevent nested scrolling of the iframe.
      <div
        className={clsx('filterContainer', 'filterContainer--inline', 'filterContainer--overlap', {
          'filterContainer--active': filterOpen,
        })}
      >
        <div className="dropdown-inner">
          {hasCategories && (
            <Select
              isClearable
              isSearchable={false}
              classNamePrefix="reactSelect"
              placeholder={i18n.translate('directoryView.category') as string}
              options={categoryOptions}
              value={
                categoryOptions.find((option) => {
                  return option.value === pendingCategory?.cat_name;
                }) ?? null
              }
              onChange={handleCategoryChange}
            />
          )}

          {pendingSubcategories.length > 0 && (
            <Select
              isClearable
              isSearchable={false}
              classNamePrefix="reactSelect"
              placeholder={i18n.translate('directoryView.subcategory') as string}
              options={pendingSubcategoryOptions}
              value={
                pendingSubcategoryOptions.find((option) => {
                  return option.value === pendingSubcategory?.sub_catname;
                }) ?? null
              }
              onChange={handleSubcategoryChange}
            />
          )}

          {children?.(pendingCategory)}

          <div className="dropdown-actions">
            <button className="show-results" onClick={handleSubmit}>
              {i18n.translate('directoryView.showResults')}
            </button>
            <button className="clear-btn" onClick={handleClear}>
              {i18n.translate('directoryView.clear')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  const handleCategoryClick = (category: Category) => {
    onCategorySelect(selectedCategory?.cat_name === category.cat_name ? null : category);
  };

  const handleSubcategoryClick = (subcategory: Subcategory) => {
    onSubcategorySelect(selectedSubcategory?.sub_catname === subcategory.sub_catname ? null : subcategory);
  };

  return (
    <div className={clsx('filterContainer', { 'filterContainer--active': filterOpen })}>
      {hasCategories && (
        <div className="checkboxOptions">
          <span className="title">{i18n.translate('directoryView.filters')}</span>

          {categories.map((category) => {
            return (
              <div key={category.cat_name} className="checkboxDiv">
                <label className="checkbox-label">
                  <span className="labelName">{category.cat_name_translated}</span>
                  <input
                    type="checkbox"
                    checked={selectedCategory?.cat_name === category.cat_name}
                    onChange={() => {
                      handleCategoryClick(category);
                    }}
                  />
                  <span className="checkmark" />
                </label>

                {selectedCategory?.cat_name === category.cat_name && category.subcategories.length > 0 && (
                  <div className="subcategory-options">
                    {category.subcategories.map((sub) => {
                      return (
                        <div key={sub.sub_catname} className="checkboxDiv subcategoryDiv">
                          <label className="checkbox-label">
                            <span className="labelName">{sub.sub_catname_translated}</span>
                            <input
                              type="checkbox"
                              checked={selectedSubcategory?.sub_catname === sub.sub_catname}
                              onChange={() => {
                                handleSubcategoryClick(sub);
                              }}
                            />
                            <span className="checkmark" />
                          </label>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {children?.(selectedCategory)}

      <div className="resetFilters" onClick={onResetFilters}>
        {i18n.translate('directoryView.resetFilters')}
      </div>

      <div className="text-center">
        <button onClick={onResetFilters} className="filter-btn inverted">
          {i18n.translate('directoryView.resetFilters')}
        </button>
        <button className="show-results" onClick={onToggleFilter}>
          {i18n.translate('directoryView.showResults')}
        </button>
      </div>
    </div>
  );
}

export default CategoryFilter;
